import {
  classifyAuthenticatedHttpError,
  createApiTransport,
  createAuthenticatedHttpClient,
  createInstanceConfig,
  createParseContext,
  DEFAULT_CAPABILITIES,
  defaultSettingsFromConfig,
  resolveDefaultInstance,
  type ApiTransport,
  type Clock,
  type InstanceConfig,
  type InstanceSource,
  type ParseContext,
} from '@visious/core';
import { createFetchHttpClient } from './http';
import { systemClock, systemRandom } from './clock';
import { setConfigDefaults } from './settings';
import { createAuthProvider, createTokenStore, type TokenStore } from './authStorage';

/**
 * §3 step 2 — the saved setting is per-origin `localStorage`. Reading it is the platform's job;
 * core only ranks the candidates it is handed.
 */
const SAVED_INSTANCE_KEY = 'visious.instance';

export interface Platform {
  readonly config: InstanceConfig;
  readonly parseContext: ParseContext;
  readonly transport: ApiTransport;
  /**
   * R1's escape hatch, handed to screen models rather than reached for: a model that has to wait
   * for the player to settle needs a timer, and `setTimeout` is a platform global.
   */
  readonly clock: Clock;
  /** Which of §3's four steps won. Shown on the settings page so a surprise is visible, not silent. */
  readonly instanceSource: InstanceSource;
  /** R9 — the platform owns the credential; core is only ever handed `headers()`. */
  readonly tokens: TokenStore;
}

function readSavedInstance(): string | null {
  try {
    return globalThis.localStorage.getItem(SAVED_INSTANCE_KEY);
  } catch {
    // Storage can throw outright in a partitioned/blocked context. That is not a boot failure.
    return null;
  }
}

/** §3 step 3 — a missing or malformed `config.json` falls through to step 4 rather than breaking boot. */
async function readConfigJson(): Promise<unknown> {
  try {
    const response = await fetch('/config.json', { cache: 'no-store' });
    if (!response.ok) return null;
    // R4 — `JSON.parse` stays outside core; core reads the parsed value defensively.
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

export type BootstrapProblem = { readonly kind: 'no-instance' } | { readonly kind: 'bad-instance'; readonly url: string };

export type BootstrapResult =
  | { readonly ok: true; readonly platform: Platform }
  | { readonly ok: false; readonly problem: BootstrapProblem };

/**
 * One platform per page load, shared by every screen.
 *
 * One, not one per screen: a second platform means a second copy of everything the instance is
 * configured with, and a screen that disagrees with the rest of the app about where it is talking.
 *
 * This is not R10's forbidden singleton — core is still a set of factories taking injected deps,
 * and this holds one *instantiation* of them. It is invalidated rather than mutated, which is what
 * the instance switch will call.
 */
let platformPromise: Promise<BootstrapResult> | null = null;

export function getPlatform(): Promise<BootstrapResult> {
  // The `?instance=` override is read once, at first boot: it is a deploy/debug escape hatch (§3
  // step 1), and re-reading it after an in-app navigation would silently drop it on the first link.
  platformPromise ??= bootstrapPlatform(globalThis.location.search);
  return platformPromise;
}

/** Drops the cached platform so the next `getPlatform()` re-resolves the instance. */
export function invalidatePlatform(): void {
  platformPromise = null;
}

export async function bootstrapPlatform(search: string): Promise<BootstrapResult> {
  const urlParam = new URLSearchParams(search).get('instance');
  const configJson = await readConfigJson();

  // The same file also carries the deployment's defaults for the Visious-specific settings —
  // handed to the settings store here because this is the one place that reads `config.json`.
  setConfigDefaults(defaultSettingsFromConfig(configJson));

  const resolved = resolveDefaultInstance({
    urlParam,
    savedSetting: readSavedInstance(),
    configJson,
    buildDefault: import.meta.env.VITE_DEFAULT_INSTANCE ?? null,
  });
  if (resolved === null) return { ok: false, problem: { kind: 'no-instance' } };

  // R2 — direct cross-origin access (§3, no proxy anywhere), so transport equals instance. Passing
  // it twice keeps that a decision at the boundary rather than a derivation inside core.
  const config = createInstanceConfig({
    instanceUrl: resolved.instanceUrl,
    transportBaseUrl: resolved.instanceUrl,
  });
  if (!config.ok) return { ok: false, problem: { kind: 'bad-instance', url: resolved.instanceUrl } };

  // R9 — a token belongs to exactly one instance, so landing on a different one revokes the rest.
  const tokens = createTokenStore(config.value.instanceUrl);
  tokens.clearOtherInstances();

  return {
    ok: true,
    platform: {
      config: config.value,
      // This is later replaced with the 5-step probe's result; until then it is the verified
      // configuration for this instance, not a guess.
      parseContext: createParseContext(config.value, DEFAULT_CAPABILITIES),
      transport: createApiTransport({
        // One transport, one auth policy: the bearer header is attached *below* retry, so
        // authenticated and public traffic share the same §3 behaviour.
        http: createAuthenticatedHttpClient({
          http: createFetchHttpClient(),
          config: config.value,
          auth: createAuthProvider(() => tokens.read()),
        }),
        clock: systemClock,
        random: systemRandom,
        // §8's 403 is overloaded. Without this the signed-out case classifies as `urls-expired`.
        classifyError: classifyAuthenticatedHttpError,
      }),
      clock: systemClock,
      instanceSource: resolved.source,
      tokens,
    },
  };
}
